import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { GoogleLogo, CircleNotch, Lock } from "@phosphor-icons/react";

const LOGIN_BG =
  "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GBE_LOGO =
  "https://customer-assets.emergentagent.com/job_aroflo-hub/artifacts/0o4hadpf_gbe-logo-with-yellow-strip.jpg";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get("next") || "/dashboard";
  const { setUser } = useAuth();
  const [mode] = useState("login"); // login only — registration removed
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA challenge state
  const [twofaToken, setTwofaToken] = useState("");
  const [twofaCode, setTwofaCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.twofa_required) {
          setTwofaToken(data.twofa_token);
          setTwofaCode("");
          setLoading(false);
          return;
        }
        setUser(data);
      } else {
        const { data } = await api.post("/auth/register", { email, password, name });
        setUser(data);
      }
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitTwofa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/2fa/login/verify", { twofa_token: twofaToken, code: twofaCode });
      setUser(data);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // Google sign-in temporarily disabled — using a custom branded OAuth app later.
    toast.info("Coming soon — please use the email and password login method for now.");
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2 bg-[#F9FAFB]" data-testid="login-page">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10 flex items-center gap-3">
            <img
              src={GBE_LOGO}
              alt="GBE Group"
              className="h-11 w-11 rounded-sm object-cover"
              data-testid="brand-logo"
            />
            <div>
              <p className="font-heading text-lg font-black tracking-tighter text-[#0A0A0A] leading-none">
                ASSET HUB
              </p>
              <p className="text-[11px] uppercase tracking-widest text-neutral-500">GBE Group</p>
            </div>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
            {twofaToken ? "Verify it's you." : mode === "login" ? "Sign in." : "Create account."}
          </h1>
          <p className="mt-3 text-base text-neutral-600">
            {twofaToken
              ? "Enter the 6-digit code from your authenticator app, or a backup code."
              : mode === "login"
              ? "Access your asset management and data analysis workspace."
              : "Register to start connecting your Aroflo data."}
          </p>

          {twofaToken ? (
            <form onSubmit={submitTwofa} className="mt-8 space-y-4" data-testid="twofa-challenge-form">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Authentication code</label>
                <input
                  data-testid="twofa-challenge-code"
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(e.target.value)}
                  autoFocus
                  placeholder="000000 or backup code"
                  className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-lg font-mono tracking-[0.3em] text-[#0A0A0A] outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
                />
              </div>
              {error && (
                <div data-testid="login-error" className="rounded-sm border border-[#DC2626]/30 bg-[#DC2626]/5 px-3.5 py-2.5 text-sm text-[#DC2626]">
                  {error}
                </div>
              )}
              <button
                data-testid="twofa-challenge-submit"
                type="submit"
                disabled={loading || !twofaCode}
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-60"
              >
                {loading && <CircleNotch size={18} className="animate-spin" />}
                Verify
              </button>
              <button
                type="button"
                data-testid="twofa-challenge-cancel"
                onClick={() => { setTwofaToken(""); setTwofaCode(""); setError(""); }}
                className="w-full text-sm font-semibold text-neutral-500 hover:underline"
              >
                Back to sign in
              </button>
            </form>
          ) : (
          <>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            {mode === "register" && (
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Full name</label>
                <input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base text-[#0A0A0A] outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
                  placeholder="Jane Smith"
                />
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base text-[#0A0A0A] outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Password</label>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base text-[#0A0A0A] outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-sm border border-[#DC2626]/30 bg-[#DC2626]/5 px-3.5 py-2.5 text-sm text-[#DC2626]">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-60"
            >
              {loading && <CircleNotch size={18} className="animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-neutral-200" />
            <span className="text-xs uppercase tracking-widest text-neutral-400">or</span>
            <div className="h-px flex-1 bg-neutral-200" />
          </div>

          <button
            data-testid="google-login-button"
            onClick={googleLogin}
            className="flex w-full items-center justify-center gap-2 rounded-sm border border-neutral-300 bg-white px-4 py-2.5 text-base font-semibold text-[#0A0A0A] transition-all duration-200 hover:bg-neutral-50"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </button>
          </>
          )}
        </div>
      </div>

      {/* Right: visual */}
      <div className="relative hidden lg:block">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0A]/70" />
        <div className="relative flex h-full flex-col justify-end p-14">
          <Lock size={36} weight="fill" className="mb-6 text-white/90" />
          <h2 className="font-heading text-3xl font-black tracking-tighter text-white">
            One secure hub for every asset record.
          </h2>
          <p className="mt-3 max-w-md text-base text-white/70">
            Connect your asset management system, choose exactly which data to pull, and edit it from a
            single, high-contrast operations console.
          </p>
        </div>
      </div>
    </div>
  );
}
